/**
 * Category Selector with auto-search and create-on-enter functionality
 *
 * Features:
 * - Type to search existing categories
 * - Enter to select matching category
 * - Enter to create new category if no match
 * - Shows category color badges
 */

import { useState, useCallback, useMemo } from 'react';
import { Select, Spin, Space, message } from 'antd';
import { PlusOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { categoriesApi } from '@api/index';

interface CategorySelectorProps {
  value?: string;
  onChange?: (value: string | undefined) => void;
  placeholder?: string;
  allowCreate?: boolean;
  disabled?: boolean;
}

export default function CategorySelector({
  value,
  onChange,
  placeholder = 'Search or create category...',
  allowCreate = true,
  disabled = false,
}: CategorySelectorProps) {
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState('');
  const [open, setOpen] = useState(false);

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => categoriesApi.create({ name }),
    onSuccess: (newCategory) => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      onChange?.(newCategory.id);
      setSearchValue('');
      setOpen(false);
      message.success(`Category "${newCategory.name}" created`);
    },
    onError: () => {
      message.error('Failed to create category');
    },
  });

  const filteredCategories = useMemo(() => {
    if (!searchValue) return categories;
    const lower = searchValue.toLowerCase();
    return categories.filter((cat) => cat.name.toLowerCase().includes(lower));
  }, [categories, searchValue]);

  const exactMatch = useMemo(() => {
    if (!searchValue) return null;
    const lower = searchValue.toLowerCase();
    return categories.find((cat) => cat.name.toLowerCase() === lower);
  }, [categories, searchValue]);

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleSelect = useCallback(
    (selectedValue: string) => {
      onChange?.(selectedValue);
      setSearchValue('');
      setOpen(false);
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && searchValue && !exactMatch && allowCreate) {
        e.preventDefault();
        e.stopPropagation();
        createMutation.mutate(searchValue);
      }
    },
    [searchValue, exactMatch, allowCreate, createMutation]
  );

  const handleClear = useCallback(() => {
    onChange?.(undefined);
    setSearchValue('');
  }, [onChange]);

  const options = useMemo(() => {
    const categoryOptions = filteredCategories.map((cat) => ({
      value: cat.id,
      label: (
        <Space>
          <span
            style={{
              display: 'inline-block',
              width: 12,
              height: 12,
              borderRadius: 2,
              backgroundColor: cat.color,
            }}
          />
          {cat.name}
        </Space>
      ),
      category: cat,
    }));

    // Add "create new" option if searching and no exact match
    if (searchValue && !exactMatch && allowCreate) {
      categoryOptions.push({
        value: `__create__:${searchValue}`,
        label: (
          <Space style={{ color: '#1890ff' }}>
            <PlusOutlined />
            Create "{searchValue}"
          </Space>
        ),
        category: null as any,
      });
    }

    return categoryOptions;
  }, [filteredCategories, searchValue, exactMatch, allowCreate]);

  const handleChange = useCallback(
    (selectedValue: string) => {
      if (selectedValue?.startsWith('__create__:')) {
        const newName = selectedValue.replace('__create__:', '');
        createMutation.mutate(newName);
      } else {
        handleSelect(selectedValue);
      }
    },
    [createMutation, handleSelect]
  );

  return (
    <Select
      showSearch
      allowClear
      value={value}
      placeholder={placeholder}
      disabled={disabled || createMutation.isPending}
      loading={isLoading || createMutation.isPending}
      filterOption={false}
      searchValue={searchValue}
      onSearch={handleSearch}
      onChange={handleChange}
      onClear={handleClear}
      onInputKeyDown={handleKeyDown}
      open={open}
      onDropdownVisibleChange={setOpen}
      options={options}
      notFoundContent={isLoading ? <Spin size="small" /> : null}
      style={{ width: '100%' }}
      suffixIcon={<AppstoreOutlined />}
      optionRender={(option) => option.label}
    />
  );
}
